import {
  Box,
  Card,
  Grid,
  Icon,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  ToggleButton,
  ToggleButtonGroup,
} from "@mui/material";
import MDAlert from "components/MDAlert";
import MDBox from "components/MDBox";
import MDInput from "components/MDInput";
import MDTypography from "components/MDTypography";
import JobModal from "components/JobModal/JobModal";
import TableSkeleton from "components/TableSkeleton/TableSkeleton";
import DefaultStatisticsCard from "examples/Cards/StatisticsCards/DefaultStatisticsCard";
import {
  OutstandingWorkReportMode,
} from "generated/graphql";
import useGetOutstandingWorkReport from "hooks/organization/reports/types/useGetOutstandingWorkReport";
import DashboardPage from "layouts/DashboardPage/DashboardPage";
import dayjs from "dayjs";
import { ChangeEvent, useCallback, useMemo, useState } from "react";
import formatCentsToUSD from "utils/money/formatCentsToUSD";

const tableLayoutSx = {
  tableLayout: "fixed" as const,
  width: "100%",
  "& .MuiTableHead-root": {
    display: "table-header-group !important",
  },
  "& .MuiTableHead-root .MuiTableRow-root": {
    display: "table-row !important",
  },
  "& .MuiTableHead-root .MuiTableCell-root": {
    display: "table-cell !important",
  },
  "& .MuiTableBody-root .MuiTableRow-root": {
    display: "table-row !important",
  },
};

const getModeLabel = (mode: OutstandingWorkReportMode) =>
  mode === OutstandingWorkReportMode.JOB_SCHEDULE
    ? "Use Job Schedule"
    : "Use Job Complete Status";

export default function ReportsOutstandingWork() {
  const today = dayjs().format("YYYY-MM-DD");
  const startOfYear = dayjs().startOf("year").format("YYYY-MM-DD");
  const [mode, setMode] = useState<OutstandingWorkReportMode>(
    OutstandingWorkReportMode.JOB_SCHEDULE
  );
  const [selectedJobId, setSelectedJobId] = useState<string | null>(null);
  const [openModal, setOpenModal] = useState(false);
  const [date, setDate] = useState({
    startDate: startOfYear,
    endDate: today,
  });
  const { data, loading, error } = useGetOutstandingWorkReport({
    variables: {
      mode,
      startDate: `${date.startDate} 00:00:00`,
      endDate: `${date.endDate} 23:59:59`,
    },
  });

  const changeDate = (event: ChangeEvent<HTMLInputElement>, type: "startDate" | "endDate") => {
    const formattedDate = event.target.value;
    setDate((previous) => ({
      ...previous,
      [type]: formattedDate,
    }));
  };

  const handleOpenModal = useCallback((jobId: string) => {
    setSelectedJobId(jobId);
    setOpenModal(true);
  }, []);

  const handleCloseModal = useCallback(() => {
    setSelectedJobId(null);
    setOpenModal(false);
  }, []);

  const jobs = useMemo(() => data?.jobs ?? [], [data]);
  const cards = useMemo(
    () => [
      {
        title: "Count of Jobs Won",
        count: data?.countJobsWon ?? 0,
      },
      {
        title: "Count of Jobs Outstanding",
        count: data?.countJobsOutstanding ?? 0,
      },
      {
        title: "Total Won Revenue",
        count: formatCentsToUSD(Number(data?.totalWonRevenue ?? 0)),
      },
      {
        title: "Total Outstanding Revenue",
        count: formatCentsToUSD(Number(data?.totalOutstandingRevenue ?? 0)),
      },
    ],
    [data]
  );

  return (
    <DashboardPage>
      <MDBox mb={6}>
        <Grid container spacing={3}>
          <Grid item xs={12}>
            <Card>
              <MDBox
                p={3}
                lineHeight={1}
                display="flex"
                alignItems="center"
                width="100%"
                justifyContent="space-between"
              >
                <Box>
                  <MDTypography variant="h4" fontWeight="medium">
                    Outstanding Work Report
                  </MDTypography>
                  <MDTypography variant="body2" color="text" mt={1}>
                    Review current won work that still needs to move through the job pipeline.
                  </MDTypography>
                </Box>
                <Box
                  sx={{
                    width: 48,
                    height: 48,
                    borderRadius: "50%",
                    backgroundColor: "info.main",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                  }}
                >
                  <Icon sx={{ color: "#fff" }}>pending_actions</Icon>
                </Box>
              </MDBox>
            </Card>
          </Grid>

          <Grid item xs={12}>
            <Card>
              <MDBox p={3} display="flex" justifyContent="space-between" alignItems="center" flexWrap="wrap" gap={2}>
                <Box>
                  <MDTypography variant="h6">Outstanding Work Mode</MDTypography>
                  <MDTypography variant="body2" color="text">
                    {data?.mode ? getModeLabel(data.mode) : "Use Job Schedule"}
                  </MDTypography>
                </Box>
                <ToggleButtonGroup
                  value={mode}
                  exclusive
                  size="small"
                  onChange={(_, nextValue) => {
                    if (nextValue) {
                      setMode(nextValue);
                    }
                  }}
                >
                  <ToggleButton value={OutstandingWorkReportMode.JOB_SCHEDULE}>
                    Use Job Schedule
                  </ToggleButton>
                  <ToggleButton value={OutstandingWorkReportMode.JOB_COMPLETE_STATUS}>
                    Use Job Complete Status
                  </ToggleButton>
                </ToggleButtonGroup>
              </MDBox>
            </Card>
          </Grid>

          <Grid item xs={12}>
            <Card>
              <MDBox p={3}>
                <Grid container spacing={2} alignItems="flex-end">
                  <Grid item xs={12} md={6}>
                    <MDTypography variant="body2" fontWeight="regular" mb={1}>
                      Proposal Won Date Start
                    </MDTypography>
                    <MDInput
                      type="date"
                      value={date.startDate}
                      onChange={(event: ChangeEvent<HTMLInputElement>) =>
                        changeDate(event, "startDate")
                      }
                      InputLabelProps={{ shrink: true }}
                      fullWidth
                    />
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <MDTypography variant="body2" fontWeight="regular" mb={1}>
                      Proposal Won Date End
                    </MDTypography>
                    <MDInput
                      type="date"
                      value={date.endDate}
                      onChange={(event: ChangeEvent<HTMLInputElement>) =>
                        changeDate(event, "endDate")
                      }
                      InputLabelProps={{ shrink: true }}
                      fullWidth
                    />
                  </Grid>
                </Grid>
              </MDBox>
            </Card>
          </Grid>

          {cards.map((card) => (
            <Grid item xs={12} md={6} lg={3} key={card.title}>
              <MDBox mb={1.5}>
                <DefaultStatisticsCard title={card.title} count={card.count} />
              </MDBox>
            </Grid>
          ))}

          <Grid item xs={12}>
            <Card>
              <MDBox p={3}>
                <MDTypography variant="h6">Outstanding Jobs</MDTypography>
                <MDTypography variant="body2" color="text">
                  Jobs shown below are outstanding based on the selected mode.
                </MDTypography>
              </MDBox>

              {loading ? (
                <MDBox p={3}>
                  <TableSkeleton numberOfLines={10} />
                </MDBox>
              ) : null}

              {error ? (
                <MDBox p={3}>
                  <MDAlert color="error">{error.message}</MDAlert>
                </MDBox>
              ) : null}

              {!loading && !error ? (
                <TableContainer>
                  <Table sx={tableLayoutSx}>
                    <TableHead>
                      <TableRow>
                        <TableCell>Job ID</TableCell>
                        <TableCell>Job Name</TableCell>
                        <TableCell>Customer Name</TableCell>
                        <TableCell align="right">Job Revenue</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {jobs.length ? (
                        jobs.map((job) => (
                          <TableRow key={job.id}>
                            <TableCell>
                              <MDTypography
                                variant="button"
                                color="info"
                                sx={{ cursor: "pointer" }}
                                onClick={() => handleOpenModal(job.id)}
                              >
                                {job.externalId}
                              </MDTypography>
                            </TableCell>
                            <TableCell>{job.jobName || "N/A"}</TableCell>
                            <TableCell>{job.customerName || "N/A"}</TableCell>
                            <TableCell align="right">
                              {formatCentsToUSD(Number(job.jobRevenue ?? 0))}
                            </TableCell>
                          </TableRow>
                        ))
                      ) : (
                        <TableRow>
                          <TableCell colSpan={4} align="center">
                            <MDTypography variant="button" color="text">
                              No outstanding jobs found for the selected mode.
                            </MDTypography>
                          </TableCell>
                        </TableRow>
                      )}
                    </TableBody>
                  </Table>
                </TableContainer>
              ) : null}
            </Card>
          </Grid>
        </Grid>
        <JobModal jobId={selectedJobId} open={openModal} onClose={handleCloseModal} />
      </MDBox>
    </DashboardPage>
  );
}
